/**
 * 목표 알림 — 화면 상단 중앙에 배치되는 작은 알약형 라벨.
 *
 *   🎯 목표 · 별 탄생시키기  ·  최고 단계 N / 10
 *
 * 단계 사다리 칩 스트립은 화면 하단에 따로 둔다 — 이 컴포넌트는 텍스트
 * 한 줄짜리 안내. 좌상 지구 내구도 / 우상 점수 와 가로 대역이 겹치지
 * 않도록 max-w 를 짧게 제한한다.
 */

import { maxTierIndex } from "../game/tier.ts";
import { t } from "../i18n/index.ts";

export interface GoalPill {
  element: HTMLDivElement;
  setPeakTier(tierIndex: number): void;
  reset(): void;
  detach(): void;
}

export function attachGoalPill(host: HTMLElement): GoalPill {
  // Internal index is 0-based; the user-facing display is 1-based.
  const maxIdx = maxTierIndex();
  const totalStages = maxIdx + 1;
  const element = document.createElement("div");
  element.dataset["testid"] = "goal-pill";
  // Mobile-first sizing — compact pill at narrow widths, full size at sm:+.
  element.className =
    "pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 flex max-w-[92vw] items-baseline gap-2 truncate rounded-full bg-black/55 px-3 py-1 font-display text-[10px] tracking-widest text-slate-200 ring-1 ring-white/10 backdrop-blur-md sm:top-3 sm:gap-3 sm:px-4 sm:py-1.5 sm:text-xs";

  const goalText = document.createElement("span");
  goalText.textContent = t("goal.label");

  const sep = document.createElement("span");
  sep.className = "text-slate-500/70";
  sep.textContent = "·";

  const peakText = document.createElement("span");
  peakText.dataset["testid"] = "goal-pill-peak";
  peakText.className = "font-bold text-cosmic-glow";
  peakText.textContent = t("goal.peak", { current: 1, total: totalStages });

  element.append(goalText, sep, peakText);
  host.appendChild(element);

  // -1 = no tier seen yet; first launch/merge brings it to 0+ which
  // displays as "1 / total" (player has the starter ammo).
  let peak = -1;

  const render = (): void => {
    const display = Math.max(0, peak) + 1;
    peakText.textContent = t("goal.peak", { current: display, total: totalStages });
    const ratio = Math.max(0, peak) / maxIdx;
    if (ratio >= 1) peakText.style.color = "#ffe066";
    else if (ratio > 0.7) peakText.style.color = "#ffc857";
    else if (ratio > 0.4) peakText.style.color = "#9ddfff";
    else peakText.style.color = "";
  };

  const setPeakTier = (tierIndex: number): void => {
    if (tierIndex <= peak) return;
    peak = Math.max(0, Math.min(maxIdx, tierIndex));
    render();
    if (typeof element.animate === "function") {
      element.animate(
        [
          { transform: "translate(-50%, 0) scale(1)" },
          { transform: "translate(-50%, 0) scale(1.10)" },
          { transform: "translate(-50%, 0) scale(1)" },
        ],
        { duration: 240, easing: "ease-out" },
      );
    }
  };

  const reset = (): void => {
    peak = -1;
    render();
  };

  const detach = (): void => {
    element.remove();
  };

  render();
  return { element, setPeakTier, reset, detach };
}
